/**
 * Value Props Service
 *
 * Frontend service for value proposition operations.
 */

import type { IClaimDecomposition, IValueProp } from '@/types/campaigns'
import { createApiRequest } from '../api/createApiRequest'

interface GenerateValuePropsRequest {
    primary_claim_id: string
    run_id?: string
    generation_notes?: string
}
void (null as any as GenerateValuePropsRequest)

interface GenerateValuePropsResponse {
    success: boolean
    job_ids: string[]
    total_jobs: number
    decomposition: IClaimDecomposition
    message: string
}
void (null as any as GenerateValuePropsResponse)

export interface ValuePropsCurationSummary {
    raw_generated: number
    kept: number
    filtered_out: number
    scoring_rejects: number
}

interface GenerationStatusResponse {
    status: 'processing' | 'complete'
    completed?: number
    failed?: number
    total: number
    /** Present while processing so polling can update list + progress bar. */
    value_props?: IValueProp[]
    decomposition?: IClaimDecomposition | null
    idle_ms_since_newest_prop?: number | null
    /** True after ~5m with no new props — some jobs may have failed; call finalizeValueProps. */
    recommend_finalize?: boolean
    finalized_partial?: boolean
    curated?: boolean
    /** Set when status is complete and server ran scoring + curation. */
    curation?: ValuePropsCurationSummary
}

export interface UpdateValuePropsRequest {
    value_props: IValueProp[]
}

export interface UpdateValuePropsResponse {
    success: boolean
    value_props: IValueProp[]
}

export interface PrimaryValuePropResponse {
    success: boolean
    value_prop: IValueProp
}

export interface ValuePropsStrategyResponse {
    success: boolean
    value_props: IValueProp[]
    primary_claim_id: string | null
    claim_decomposition: IClaimDecomposition | null
    last_generated_at?: string | null
    generation_notes?: string | null
    batch_expected_count?: number | null
}

/**
 * Load persisted value props from the library profile (Mongo).
 */
export async function getValuePropsStrategy(
    profileType: 'product' | 'company' | 'person',
    profileId: string
): Promise<ValuePropsStrategyResponse> {
    return createApiRequest<ValuePropsStrategyResponse>({
        url: `/campaigns/library/${profileType}/${profileId}/value-props`,
        method: 'GET',
    })
}

/**
 * Generate value props from a primary claim
 */
export async function generateValueProps(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    request: GenerateValuePropsRequest
): Promise<GenerateValuePropsResponse> {
    const data = await createApiRequest<GenerateValuePropsResponse>({
        url: `/campaigns/library/${profileType}/${profileId}/value-props/generate`,
        method: 'POST',
        data: request as any,
    })

    return data
}

/**
 * Check generation status (for polling)
 */
export async function getGenerationStatus(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    jobIds: string,
    primaryClaimId: string
): Promise<GenerationStatusResponse> {
    const qs = new URLSearchParams({
        job_ids: jobIds,
        primary_claim_id: String(primaryClaimId),
    })
    const data = await createApiRequest<GenerationStatusResponse>({
        url: `/campaigns/library/${profileType}/${profileId}/value-props/status?${qs.toString()}`,
        method: 'GET',
    })

    return data
}

/**
 * Update value props (edit, priority, primary selection)
 */
export async function updateValueProps(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    request: UpdateValuePropsRequest
): Promise<UpdateValuePropsResponse> {
    const data = await createApiRequest<UpdateValuePropsResponse>({
        url: `/campaigns/library/${profileType}/${profileId}/value-props`,
        method: 'PATCH',
        data: request as any,
    })

    return data
}

/**
 * Get primary value prop
 */
/**
 * Score + curate partial results when the queue did not deliver all jobs.
 */
export async function finalizeValueProps(
    profileType: 'product' | 'company' | 'person',
    profileId: string,
    primaryClaimId: string
): Promise<{
    success: boolean
    value_props: IValueProp[]
    decomposition?: IClaimDecomposition
    already_final?: boolean
    curation?: ValuePropsCurationSummary
}> {
    return createApiRequest({
        url: `/campaigns/library/${profileType}/${profileId}/value-props/finalize`,
        method: 'POST',
        data: { primary_claim_id: String(primaryClaimId) },
    })
}

export async function getPrimaryValueProp(
    profileType: 'product' | 'company' | 'person',
    profileId: string
): Promise<PrimaryValuePropResponse> {
    const data = await createApiRequest<PrimaryValuePropResponse>({
        url: `/campaigns/library/${profileType}/${profileId}/value-props/primary`,
        method: 'GET',
    })

    return data
}

export const valuePropsService = {
    getValuePropsStrategy,
    generateValueProps,
    getGenerationStatus,
    finalizeValueProps,
    updateValueProps,
    getPrimaryValueProp,
}
